import { LoadingButton } from "@mui/lab";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Grid,
    InputAdornment,
    useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Field, Formik } from "formik";
import { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { AESDecryptURL } from "utils/encryption/AESDecrypt";
import { editCustomer } from "../../../../utils/action";
import {
    InputSelect,
    InputText,
    setTitleCase,
} from "../../../components/template/partials";
import useAuth from "../../../hooks/useAuth";

// REDUX
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { getCustomerByID } from "../../../redux/customer";
import {
    getBuildingTypes,
    getDistrics,
    getUsers,
    getVillages,
} from "../../../redux/dropdown";
import { validationSchema } from "../CustomerUIHelpers";

// inital values form
const initialValues = (autoComplete, initForm) => {
    return {
        username: initForm?.username ?? null,
        id_number: autoComplete?.nik ?? "",
        customer_name: autoComplete?.nama_pelanggan ?? "",
        phone_number: autoComplete?.nomor_hp ?? "",
        total_occupants: autoComplete?.jumlah_penghuni ?? "",
        building_type: autoComplete?.jenis_bangunan ?? null,
        district: initForm?.district ?? null,
        village: initForm?.village ?? null,
        address: autoComplete?.alamat_detail ?? "",
        volume_tangki_septik: autoComplete?.volume_tangki_septik ?? "",
        tangki_septic_location: autoComplete?.lokasi_tangki_septik ?? {
            latitude: "",
            longitude: "",
        },
    };
};

export function EditCustomer() {
    const [isLoading, setLoading] = useState(false);
    const [initForm, setInitForm] = useState(null);
    const theme = useTheme();
    const location = useLocation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const { id } = useParams();
    const { isStrictAccess, authData } = useAuth();
    const {
        dropdownUsersData,
        dropdownDistrictsData,
        dropdownVillagesData,
        dropdownBuildingTypesData,
        getCustomerByIDData,
    } = useSelector(
        (state) => ({
            dropdownUsersData: state.DropdownUsersData,
            dropdownDistrictsData: state.DropdownDistrictsData,
            dropdownVillagesData: state.DropdownVillagesData,
            dropdownBuildingTypesData: state.DropdownBuildingTypesData,
            getCustomerByIDData: state.GetCustomerByIDData,
        }),
        shallowEqual
    );
    const { data } = getCustomerByIDData;
    const showDialog = location.state?.showDialog ?? false;
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
    const decID = parseInt(AESDecryptURL(id));

    useEffect(() => {
        if (isStrictAccess) {
            dispatch(getUsers());
        }
        dispatch(getDistrics());
        dispatch(getVillages());
        dispatch(getBuildingTypes());
        dispatch(getCustomerByID(decID));
    }, [dispatch, isStrictAccess, decID]);

    const onHide = () => {
        navigate(-1);
    };

    const handleFormSubmit = async (values) => {
        setLoading(true);
        const request = {
            nama_pelanggan: values.customer_name,
            nomor_hp: values.phone_number,
            nik: values.id_number,
            nama_user: isStrictAccess
                ? values.username?.username
                : authData.username,
            kecamatan: values.district?.NamaKecamatan,
            kelurahan: values.village?.NamaDesa,
            alamat_detail: values.address,
            jumlah_penghuni: values.total_occupants,
            jenis_bangunan: values.building_type,
            volume_tangki_septik: values.volume_tangki_septik,
            lokasi_tangki: values.tangki_septic_location,
        };

        // send to backend
        try {
            await editCustomer(decID, request);
            toast.success(setTitleCase("berhasil mengedit data pelanggan"));
        } catch (error) {
            toast.error(setTitleCase(error.response.data?.message));
        }
        setLoading(false);
        onHide();
    };

    useEffect(() => {
        const autoCompleteUsername = dropdownUsersData.data?.find(
            (item) => item.username === data?.nama_user
        );
        const autoCompleteDistrict = dropdownDistrictsData.data?.find(
            (item) => item.NamaKecamatan === data?.kecamatan
        );
        const autoCompleteVilage = dropdownVillagesData.data?.find(
            (item) => item.NamaDesa === data?.kelurahan
        );
        const reqInitForm = {
            username: autoCompleteUsername,
            district: autoCompleteDistrict,
            village: autoCompleteVilage,
        };
        setInitForm(reqInitForm);
    }, [data, dropdownUsersData, dropdownDistrictsData, dropdownVillagesData]);

    return (
        <Dialog
            fullScreen={fullScreen}
            fullWidth
            open={showDialog}
            onClose={onHide}
            aria-labelledby="responsive-dialog-create-customer-title"
        >
            <DialogTitle id="responsive-dialog-create-customer-title">
                Edit Pelanggan
            </DialogTitle>
            <Divider />
            <Formik
                enableReinitialize
                onSubmit={handleFormSubmit}
                initialValues={initialValues(data, initForm)}
                validationSchema={validationSchema}
            >
                {({ handleSubmit, values }) => (
                    <form onSubmit={handleSubmit}>
                        <DialogContent>
                            <Grid container spacing={3}>
                                <Grid item md={12} xs={12}>
                                    <Field
                                        component={InputText}
                                        name="id_number"
                                        label="NIK"
                                        size="small"
                                        variant="outlined"
                                        type="text"
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item md={12} xs={12}>
                                    <Field
                                        component={InputText}
                                        name="customer_name"
                                        label="Nama Pelanggan"
                                        size="small"
                                        variant="outlined"
                                        type="text"
                                        fullWidth
                                    />
                                </Grid>
                                {isStrictAccess && (
                                    <Grid item md={12} xs={12}>
                                        <Field
                                            component={InputSelect}
                                            options={
                                                dropdownUsersData?.data ?? []
                                            }
                                            isLoading={
                                                dropdownUsersData?.isLoading
                                            }
                                            getOptionLabel={(option) =>
                                                `${option?.nama_lengkap} / ${option?.username}` ??
                                                ""
                                            }
                                            isOptionEqualToValue={(
                                                option,
                                                value
                                            ) =>
                                                option?.value ===
                                                    value?.value ?? ""
                                            }
                                            name="username"
                                            label="Nama User"
                                            size="small"
                                            variant="outlined"
                                            type="text"
                                            fullWidth
                                        />
                                    </Grid>
                                )}
                                <Grid item md={6} xs={12}>
                                    <Field
                                        component={InputText}
                                        name="customer_name"
                                        label="Nama Pelanggan"
                                        size="small"
                                        variant="outlined"
                                        type="text"
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    <Field
                                        component={InputText}
                                        name="phone_number"
                                        label="No.Telepon"
                                        size="small"
                                        variant="outlined"
                                        type="text"
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    <Field
                                        component={InputText}
                                        name="total_occupants"
                                        label="Total Penghuni"
                                        size="small"
                                        variant="outlined"
                                        type="number"
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    <Field
                                        component={InputSelect}
                                        options={
                                            dropdownBuildingTypesData?.data ??
                                            []
                                        }
                                        isLoading={
                                            dropdownBuildingTypesData?.isLoading
                                        }
                                        getOptionLabel={(option) =>
                                            option ?? ""
                                        }
                                        isOptionEqualToValue={(option, value) =>
                                            option?.value === value?.value
                                        }
                                        name="building_type"
                                        label="Jenis Bangunan"
                                        size="small"
                                        variant="outlined"
                                        type="text"
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    <Field
                                        component={InputSelect}
                                        options={
                                            dropdownDistrictsData?.data ?? []
                                        }
                                        isLoading={
                                            dropdownDistrictsData?.isLoading
                                        }
                                        getOptionLabel={(option) =>
                                            option?.NamaKecamatan ?? ""
                                        }
                                        isOptionEqualToValue={(option, value) =>
                                            option?.value === value?.value
                                        }
                                        name="district"
                                        label="Kecamatan"
                                        size="small"
                                        variant="outlined"
                                        type="text"
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item md={6} xs={12}>
                                    <Field
                                        disabled={
                                            values.district?.IDKecamatan ===
                                            undefined
                                        }
                                        component={InputSelect}
                                        options={
                                            dropdownVillagesData?.data?.filter(
                                                (item) =>
                                                    values.district
                                                        ?.IDKecamatan ===
                                                    item.IDKecamatan
                                            ) ?? []
                                        }
                                        isLoading={
                                            dropdownVillagesData?.isLoading
                                        }
                                        getOptionLabel={(option) =>
                                            option?.NamaDesa ?? ""
                                        }
                                        isOptionEqualToValue={(option, value) =>
                                            option?.value === value?.value
                                        }
                                        name="village"
                                        label="Desa"
                                        size="small"
                                        variant="outlined"
                                        type="text"
                                        fullWidth
                                    />
                                </Grid>
                                <Grid item md={12} xs={12}>
                                    <Field
                                        component={InputText}
                                        name="address"
                                        label="Alamat"
                                        size="small"
                                        variant="outlined"
                                        type="text"
                                        fullWidth
                                        multiline
                                        maxRows={4}
                                    />
                                </Grid>
                                <Grid item md={12} xs={12}>
                                    <Field
                                        component={InputText}
                                        name="volume_tangki_septik"
                                        label="Volume Tangki Septik"
                                        size="small"
                                        variant="outlined"
                                        type="number"
                                        fullWidth
                                        InputProps={{
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    m³
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Grid>
                                <Grid item md={6} xs={6}>
                                    <Field
                                        component={InputText}
                                        name="tangki_septic_location.latitude"
                                        label="Lokasi Tangki Septik"
                                        size="small"
                                        variant="outlined"
                                        type="text"
                                        fullWidth
                                        multiline
                                        maxRows={4}
                                    />
                                </Grid>
                                <Grid item md={6} xs={6}>
                                    <Field
                                        component={InputText}
                                        name="tangki_septic_location.longitude"
                                        label="Lokasi Tangki Septik"
                                        size="small"
                                        variant="outlined"
                                        type="text"
                                        fullWidth
                                        multiline
                                        maxRows={4}
                                    />
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <Divider />
                        <DialogActions>
                            <Button
                                autoFocus
                                onClick={onHide}
                                color="error"
                                variant="contained"
                            >
                                Cancel
                            </Button>
                            <LoadingButton
                                autoFocus
                                loading={isLoading}
                                type="submit"
                                color="primary"
                                variant="contained"
                            >
                                Save
                            </LoadingButton>
                        </DialogActions>
                    </form>
                )}
            </Formik>
        </Dialog>
    );
}
